import { initializeApp } from 'firebase/app';

import {
  connectAuthEmulator,
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
} from 'firebase/auth';
import { connectFirestoreEmulator, initializeFirestore } from 'firebase/firestore';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = initializeFirestore(app, { ignoreUndefinedProperties: true });
export const storage = getStorage(app);

// Check if using emulators
const isUsingEmulators = process.env.REACT_APP_ENV === 'Development' && process.env.REACT_APP_USE_EMULATORS === 'true';

// Connect to emulators BEFORE setting up auth listeners
if (isUsingEmulators) {
  console.log('🔧 Connecting to Firebase Emulators...');

  try {
    connectFirestoreEmulator(db, process.env.REACT_APP_LOCAL_IP || '127.0.0.1', 8080);
    console.log('✅ Connected to Firestore Emulator on port 8080');
  } catch (error) {
    console.warn('⚠️ Failed to connect to Firestore Emulator:', error);
  }

  try {
    connectStorageEmulator(storage, process.env.REACT_APP_LOCAL_IP || '127.0.0.1', 9199);
    console.log('✅ Connected to Storage Emulator on port 9199');
  } catch (error) {
    console.warn('⚠️ Failed to connect to Storage Emulator:', error);
  }

  try {
    connectAuthEmulator(auth, process.env.REACT_APP_LOCAL_IP || '127.0.0.1', { disableWarnings: true });
    console.log('✅ Connected to Auth Emulator on port 9099');
  } catch (error) {
    console.warn('⚠️ Failed to connect to Auth Emulator:', error);
  }
}

// Token refresh function
export const refreshFirebaseToken = async () => {
  try {
    const authToken = localStorage.getItem('Authorization');
    if (!authToken) {
      console.error('No authorization token found in localStorage');
      return false;
    }

    console.log('Attempting to refresh Firebase token...');

    // Get fresh token from your backend
    const response = await fetch(`${process.env.REACT_APP_API_URL}auth/firebase-token`, {
      headers: {
        authorization: authToken,
      },
    });

    if (!response.ok) {
      console.error('Failed to get Firebase token:', response.status, response.statusText);
      return false;
    }

    const { token } = await response.json();

    if (!token) {
      console.error('No token received from refresh endpoint');
      return false;
    }

    // Sign in with custom token
    await signInWithCustomToken(auth, token);
    console.log('Successfully refreshed Firebase token');

    return true;
  } catch (error) {
    console.error('Failed to refresh Firebase token:', error);
    return false;
  }
};

// Auto-refresh token when it's about to expire
let tokenCheckInterval: NodeJS.Timeout | null = null;

onAuthStateChanged(auth, (user) => {
  // Clear existing interval
  if (tokenCheckInterval) {
    clearInterval(tokenCheckInterval);
    tokenCheckInterval = null;
  }

  if (user) {
    // Skip token refresh checks when using emulators
    console.log('process.env.REACT_APP_USE_EMULATORS', process.env.REACT_APP_USE_EMULATORS);
    if (isUsingEmulators) {
      console.log('🧪 Using Firebase emulators - skipping token refresh checks');
      return;
    }

    // Check token expiration every minute (production only)
    tokenCheckInterval = setInterval(async () => {
      try {
        const token = await user.getIdToken();
        const decoded = JSON.parse(atob(token.split('.')[1]));
        const expiresIn = decoded.exp * 1000 - Date.now();

        // Refresh if token expires in less than 5 minutes
        if (expiresIn < 5 * 60 * 1000) {
          console.log('Token expiring soon, refreshing...');
          await refreshFirebaseToken();
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
        // If we can't get the token, try to refresh
        await refreshFirebaseToken();
      }
    }, 60 * 1000);
  }
});

// Optional: Connect to emulators in development
if (process.env.REACT_APP_ENV === 'Development' && process.env.REACT_APP_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, `http://${process.env.REACT_APP_LOCAL_IP || '127.0.0.1'}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, process.env.REACT_APP_LOCAL_IP || '127.0.0.1', 8080);
  connectStorageEmulator(storage, process.env.REACT_APP_LOCAL_IP || '127.0.0.1', 9199);
}
